import React from 'react';
import { Progress as AntProgress } from 'antd';
import { CircleCheckFilled, CircleCloseFilled, CloseLarge, Check } from '@nackle/origami-icons';

/**
 * Progress component that wraps Ant Design's Progress component with additional functionality.
 * 
 * {@link https://ant.design/components/progress#api}
 *
 * @param {Object} props - The component props
 * @param {string} [props.size='medium'] - Size of the progress component ('small', 'medium', 'large')
 * @param {string} [props.status] - Status of the progress ('success', 'exception', 'incomplete')
 * @param {string} [props.type] - Type of progress bar ('line', 'circle', 'dashboard')
 * @param {number} [props.percent] - Percentage of completion (0-100)
 * @param {boolean} [props.displayPercent] - Whether to display the percentage value
 * @param {boolean} [props.isPrimary] - Whether to use primary styling
 * @param {boolean} [props.noIcon] - Whether to hide the status icon
 * @returns {React.ReactElement} A Progress component
 */
const Progress = ( { ...props } ) => {
    let { size, status, type, percent, displayPercent, isPrimary, noIcon } = props;

    if ( !size ) {
        size = 'medium';
    }
    if ( isPrimary === true ) {
        if ( props.className ) {
            props.className += ' primary';
        } else {
            props.className = 'primary';
        }
    }

    /**
     * Maps size prop values to width/height pixel values
     * 
     * @param {string} size - Size of the progress component ('small', 'medium', 'large')
     * @returns {number} Width/height in pixels
     */
    const circleStyleSize = ( size ) => {
        switch ( size ) {
            case 'small':
                return 76;
            case 'medium':
                return 120;
            case 'large':
                return 154;
            default:
                return 120;
        }
    };

    /**
     * Maps size prop values to circle stroke width values to end up with a 10px stroke every time
     * 
     * @param {string} size - Size of the progress component ('small', 'medium', 'large')
     * @returns {number} Stroke width in pixels
     */
    const circleStrokeWidth = ( size ) => {
        switch ( size ) {
            case 'small':
                return 15;
            case 'medium':
                return 9;
            case 'large':
                return 7;
            default:
                return 9;
        }
    };

    if ( type === 'circle' || type === 'dashboard' ) {

        if ( type === 'dashboard' ) {
            if ( props.className ) {
                props.className += ' ant-progress-dashboard';
            } else {
                props.className = 'ant-progress-dashboard';
            }
        }

        //Update icons for success and exception statuses
        if ( ( ( status && status === 'success' ) || percent && percent === 100 ) && !displayPercent ) {
            props.format = () => <Check />;
        } else if ( ( status && status === 'exception' ) && !displayPercent ) {
            props.format = () => <CloseLarge />;
        } else if ( displayPercent && !props.format ) {
            props.format = () => percent;
            if ( props.className ) {
                props.className += ' ant-progress-small-percent';
            } else {
                props.className = 'ant-progress-small-percent';
            }
        }

        //Add class in order to target percentage symbol
        if ( ( ( percent && percent < 100 || percent === 0 )
            || percent > 100 )
            && !props.format ) { //Allows us to make the % sign smaller, will ignore if format is set to something custom
            props.format = () => percent;
            if ( props.className ) {
                props.className += ' ant-progress-small-percent';
            } else {
                props.className = 'ant-progress-small-percent';
            }
        }
        if ( size ) {
            const componentSize = circleStyleSize( size );
            const strokeSize = circleStrokeWidth( size );
            props.strokeWidth = strokeSize;
            props.size = componentSize;
            if ( props.className ) {
                props.className += ` ant-progress-circle-${ size }`;
            } else {
                props.className = `ant-progress-circle-${ size }`;
            }
        }

        if ( status ) {
            if ( status === 'incomplete' ) {
                props.strokeColor = '#DE1B1B';
            }
        }
    } else { //linear and steps versions
        if ( size ) {
            if ( props.className ) {
                props.className += ` ant-progress-${ size }`;
            } else {
                props.className = `ant-progress-${ size }`;
            }
        }
        if ( ( ( status && status === 'success' ) || percent && percent === 100 ) && !noIcon ) {
            props.format = () => <CircleCheckFilled />;
        } else if ( noIcon ) {
            props.format = ()=> `${ percent }%`;
            if ( props.className ) {
                props.className += ' show-percent no-icon';
            } else {
                props.className = 'show-percent no-icon';
            }
        } else if ( status && status === 'exception' ) {
            props.format = () => <CircleCloseFilled />;
        }
    }
    const progressProps = props;
    delete progressProps.displayPercent;
    delete progressProps.isPrimary;
    delete progressProps.noIcon;
    return (
        <AntProgress { ...progressProps } ></AntProgress>
    );
};


export { Progress };
